/**
 * The discrete value+unit fact row where EVERY fact must survive.
 *
 * `3 beds · 2 baths · 1,450 sqft` is three facts, not one string, and the
 * difference shows the moment the row narrows: a joined string truncates
 * mid-pair and leaves `3 beds · 2 baths · 1,4…`, which is a number a reader
 * cannot use. Here each cell is `flex: 0 0 auto`, nothing wraps mid-pair, no
 * single fact truncates, and what does not fit folds into a `+n` that carries
 * the rest in its title.
 *
 * A cell's identity is its POSITION. The strip's contract (§3, `facts: readonly
 * { value, unit?, icon? }[]`) carries no id, and the rendered text is not one:
 * `2 baths` and `2 beds` are distinct, but `2` and `2` are the same string in a
 * strip whose facts have no units, so a text key silently collides and React
 * drops a cell. Worse, a key that changes with the value makes every refreshed
 * figure a NEW element — the cell is unmounted and remounted, which throws away
 * its DOM state and restarts any transition on it, so the one thing a fact strip
 * must do quietly is the one thing it did loudly.
 */
"use client";
import type { ReactElement } from "react";
import {
  CRC_NUMERIC_STYLE,
  CRC_TEXT,
  CRC_TEXT_FAINT,
} from "./cresultcard.tokens";
import type { CresultcardFactSpec } from "./cresultcard.types";

const STRIP_GAP = 10;
const GLYPH_SIZE = 11;
const GLYPH_STROKE = 1.3;
const OVERFLOW_PREFIX = "+";
const TITLE_SEPARATOR = " · ";
const UNIT_GAP = 3;

interface CresultcardFactStripProps {
  readonly facts: readonly CresultcardFactSpec[];
  /** How many cells are drawn before the rest fold into `+n`. */
  readonly max: number;
}

function factText(fact: CresultcardFactSpec): string {
  return fact.unit == null
    ? String(fact.value)
    : `${String(fact.value)} ${fact.unit}`;
}

export function CresultcardFactStrip({
  facts,
  max,
}: CresultcardFactStripProps): ReactElement | null {
  if (facts.length === 0) {
    return null;
  }

  const visible = facts.slice(0, max);
  const hidden = facts.slice(max);

  return (
    <div
      data-el="Cresultcard/FactStrip/Root"
      style={{
        display: "flex",
        alignItems: "center",
        gap: STRIP_GAP,
        minWidth: 0,
      }}
    >
      {visible.map((fact, position) => {
        const Glyph = fact.icon;
        return (
          <span
            data-el="Cresultcard/FactStrip/Entry"
            // By position, because a fact on this strip has no name of its own —
            // only a value and a unit. One shared path across every fact is a
            // measurement that cannot say WHICH fact it measured.
            key={position}
            style={{
              ...CRC_NUMERIC_STYLE,
              display: "inline-flex",
              alignItems: "baseline",
              gap: UNIT_GAP,
              flex: "0 0 auto",
              whiteSpace: "nowrap",
            }}
          >
            {Glyph != null && (
              <Glyph
                size={GLYPH_SIZE}
                stroke={GLYPH_STROKE}
                style={{ alignSelf: "center", color: CRC_TEXT_FAINT }}
              />
            )}
            <strong
              style={{
                fontSize: 11,
                fontWeight: 730,
                letterSpacing: "-0.012em",
                color: CRC_TEXT,
              }}
            >
              {fact.value}
            </strong>
            {fact.unit != null && (
              <span
                style={{
                  fontSize: 9.5,
                  fontWeight: 540,
                  color: CRC_TEXT_FAINT,
                }}
              >
                {fact.unit}
              </span>
            )}
          </span>
        );
      })}
      {hidden.length > 0 && (
        <span
          style={{
            flex: "0 0 auto",
            fontSize: 9.5,
            fontWeight: 620,
            color: CRC_TEXT_FAINT,
          }}
          title={hidden.map(factText).join(TITLE_SEPARATOR)}
        >
          {`${OVERFLOW_PREFIX}${String(hidden.length)}`}
        </span>
      )}
    </div>
  );
}
